import { C, FONT } from "./theme";

// Marketing-surface interactive primitives (landing page, hero, and any
// future public-facing page). Distinct from the denser, rectangular Button
// in FormKit.js, which is built for signup/onboarding forms — this is the
// full pill/Apple-CTA treatment used on public marketing surfaces.
//
// Hover/active/focus states are plain CSS (:hover, :active, :focus-visible,
// :disabled) via the classes below rather than JS-tracked state, matching
// how the rest of this app already styles interactive elements and
// avoiding any hover-state flicker on first paint.

export const marketingButtonStyles = `
  .pill-btn { font-family: ${FONT}; font-weight: 500; letter-spacing: -.1px; border: none; border-radius: 980px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: background .18s ease, border-color .18s ease, color .18s ease, transform .12s ease, opacity .18s ease; }
  .pill-btn:active:not(:disabled) { transform: scale(.97); }
  .pill-btn:disabled { opacity: .45; cursor: default; }
  .pill-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 3px; }
  .pill-btn-primary { background: ${C.accent}; color: #fff; }
  .pill-btn-primary:hover:not(:disabled) { background: ${C.accentHover}; }
  .pill-btn-secondary { background: transparent; color: ${C.text}; border: 1px solid ${C.border}; }
  .pill-btn-secondary:hover:not(:disabled) { border-color: ${C.text}; background: ${C.bgAlt}; }

  .text-link-btn { font-family: ${FONT}; background: none; border: none; padding: 0; color: ${C.accent}; font-size: 15.5px; font-weight: 400; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; transition: opacity .15s ease; }
  .text-link-btn:hover:not(:disabled) { text-decoration: underline; }
  .text-link-btn:disabled { opacity: .45; cursor: default; }
  .text-link-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 3px; border-radius: 4px; }

  .nav-link-btn { font-family: ${FONT}; background: none; border: none; padding: 0; color: ${C.text}; font-size: 14px; font-weight: 400; opacity: .72; cursor: pointer; transition: opacity .15s ease; }
  .nav-link-btn:hover:not(:disabled) { opacity: 1; }
  .nav-link-btn:disabled { opacity: .3; cursor: default; }
  .nav-link-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 3px; border-radius: 4px; }

  @keyframes btn-spin { to { transform: rotate(360deg); } }
  .btn-spinner { width: 13px; height: 13px; border-radius: 50%; display: inline-block; animation: btn-spin .7s linear infinite; }
`;

export function PillButton({ children, variant = "primary", size = "md", loading = false, disabled = false, style, ...props }) {
  const sizes = {
    sm: { padding: "9px 20px", fontSize: 14 },
    md: { padding: "11px 24px", fontSize: 15 },
    lg: { padding: "13px 28px", fontSize: 16 },
  };
  return (
    <button
      type="button"
      className={`pill-btn pill-btn-${variant}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      style={{ ...sizes[size], ...style }}
      {...props}
    >
      {loading && (
        <span
          className="btn-spinner"
          style={{
            border: `2px solid ${variant === "primary" ? "rgba(255,255,255,.4)" : "rgba(0,113,227,.25)"}`,
            borderTopColor: variant === "primary" ? "#fff" : C.accent,
          }}
        />
      )}
      {children}
    </button>
  );
}

export function TextLinkButton({ children, disabled = false, style, ...props }) {
  return (
    <button type="button" className="text-link-btn" disabled={disabled} style={style} {...props}>
      {children}
    </button>
  );
}

export function NavLinkButton({ children, disabled = false, style, ...props }) {
  return (
    <button type="button" className="nav-link-btn" disabled={disabled} style={style} {...props}>
      {children}
    </button>
  );
}
